import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import API from 'api/api';

const VERIFICATION_POLL_INTERVAL_MS = 30000;

export function useGetUserVerificationStatus() {
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const { uid } = useFirebaseAuth();
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getUserVerificationStatus = useCallback(async ({ silent = false } = {}) => {
    if (!uid) return; // Wait until UID is available
    if (mountedRef.current && !silent) {
      setLoading(true);
      setError(null);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await API.get(`/users/${uid}/verification-status`, {
        signal: controller.signal,
      });

      if (mountedRef.current) setVerificationStatus(response?.data);
      return response?.data;
    } catch (err) {
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return null;
      if (mountedRef.current) setError(err);
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    getUserVerificationStatus(); // Initial fetch

    const intervalId = setInterval(() => {
      getUserVerificationStatus({ silent: true });
    }, VERIFICATION_POLL_INTERVAL_MS); // Re-fetch every 30 seconds

    return () => clearInterval(intervalId); // Clear interval on unmount
  }, [getUserVerificationStatus]);

  return {
    loading,
    error,
    getUserVerificationStatus,
    verificationStatus,
  };
}
